import type { BookFormDraft, BookLanguageMapPayloadEntry } from './types';
import { defaultPageLangContent } from './model';

function normalizeWriterEmailPayload(raw: string): string {
  return raw.trim().toLowerCase();
}

function bookTitleMapToPayload(map: Record<string, string>): Record<string, string> {
  const name_map: Record<string, string> = {};
  for (const [code, raw] of Object.entries(map)) {
    const t = raw.trim();
    if (t) name_map[code] = t;
  }
  return name_map;
}

/** Request body for create/update; aligns with `BookModel.language_map` entries. */
export function bookDraftToApiBody(draft: BookFormDraft): {
  author_id: string;
  categories: string[];
  age_groups: string[];
  name_map: Record<string, string>;
  cover_image_path: string;
  promo_image_path: string;
  language_map: Record<string, BookLanguageMapPayloadEntry>;
  writer_emails: string[];
} {
  const language_map: Record<string, BookLanguageMapPayloadEntry> = {};
  const sortedPages = [...draft.pages].sort((a, b) => a.page_number - b.page_number);

  for (const [langId, on] of Object.entries(draft.language_enabled)) {
    if (!on) continue;
    const pages = sortedPages.map((p) => {
      const lc = p.byLang[langId] ?? defaultPageLangContent();
      return {
        page_number: p.page_number,
        image_path: p.image_path.trim(),
        video_path: p.video_path.trim(),
        full_text: lc.full_text.trim(),
      };
    });
    language_map[langId] = {
      lang: langId,
      title: (draft.name_map[langId] ?? '').trim(),
      description: (draft.description_map[langId] ?? '').trim(),
      pages,
    };
  }

  return {
    author_id: draft.author_id.trim(),
    categories: [...draft.category_ids],
    age_groups: [...draft.age_group_ids],
    name_map: bookTitleMapToPayload(draft.name_map),
    cover_image_path: draft.cover_image_path.trim(),
    promo_image_path: draft.promo_image_path.trim(),
    language_map,
    writer_emails: Array.from(
      new Set((draft.writer_emails ?? []).map((e) => normalizeWriterEmailPayload(String(e))).filter((e) => e)),
    ),
  };
}

/** POST `/books` body (numeric ids for DynamoDB). */
export function bookDraftToCreateApiPayload(draft: BookFormDraft): {
  author_id: number;
  categories: number[];
  age_groups: number[];
  cover_image_path: string;
  promo_image_path: string;
  language_map: Record<string, BookLanguageMapPayloadEntry>;
  writer_emails: string[];
} {
  const body = bookDraftToApiBody(draft);
  const author_id = parseInt(body.author_id, 10);
  if (!Number.isFinite(author_id)) {
    throw new Error('Зохиолчийн ID буруу байна.');
  }
  const categories = body.categories
    .map((id) => parseInt(id, 10))
    .filter((n) => Number.isFinite(n));
  const age_groups = body.age_groups
    .map((id) => parseInt(id, 10))
    .filter((n) => Number.isFinite(n));
  if (Object.keys(body.language_map).length === 0) {
    throw new Error('Идэвхтэй хэлийн language_map хоосон байна.');
  }
  return {
    author_id,
    categories,
    age_groups,
    cover_image_path: body.cover_image_path,
    promo_image_path: body.promo_image_path,
    language_map: body.language_map,
    writer_emails: body.writer_emails,
  };
}

/** PUT `/books` body: existing `id` plus fields from the draft (numeric ids). */
export function bookDraftToUpdateApiPayload(
  draft: BookFormDraft,
  options?: { omitWriterEmails?: boolean },
): {
  id: string;
  author_id: number;
  categories: number[];
  age_groups: number[];
  cover_image_path: string;
  promo_image_path: string;
  language_map: Record<string, BookLanguageMapPayloadEntry>;
  writer_emails?: string[];
} {
  const id = draft.id.trim();
  if (!id) {
    throw new Error('Номын ID байхгүй.');
  }
  const inner = bookDraftToCreateApiPayload(draft);
  if (options?.omitWriterEmails) {
    const { writer_emails: _we, ...rest } = inner;
    return { id, ...rest };
  }
  return { id, ...inner };
}

export function bookDraftCompleteForServerSave(draft: BookFormDraft): boolean {
  if (draft.mode !== 'create') return false;
  if (!draft.author_id.trim()) return false;
  if (draft.category_ids.length === 0 || draft.age_group_ids.length === 0) return false;
  if (!draft.cover_image_path.trim() && !draft.cover_image_file) return false;
  const body = bookDraftToApiBody(draft);
  return Object.keys(body.language_map).length > 0;
}
